// Cross page events are events that are fired immediately before a page transition,
// for example when a "Submit" button is pressed. The danger is that either the
// page transition will be delayed by waiting on the event network call, or
// if the event is not awaited and the page tears itself down, the network call may
// not finish causing events to never be registered.

// Instead, the `fireCrossPageEvent` function puts an event into the browser's
// localStorage, then the `dispatchCrossPageEvents` function loads those events
// on the next page load (of any page) and calls `dataLayer.push`, making the event
// network call at that time.

import { analyticsEventToDataLayerEvent } from '@rover/rsdk/src/modules/Analytics';
import type { AnalyticsEvent } from '@rover/types/src/Analytics';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';
import { localStorage } from '@rover/utilities/storage';

const CROSS_PAGE_EVENTS_NAMESPACE = 'cross-page-event:';

// This function should be called once on every page load to dispatch events that were queued on the previous page
export const dispatchCrossPageEvents = async (): Promise<void> => {
  const dataLayerPromises: Array<Promise<void>> = [];

  if (!localStorage) return;

  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(CROSS_PAGE_EVENTS_NAMESPACE)) keys.push(key);
  }

  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const event = localStorage.getItem(key);
    if (!event) continue; // eslint-disable-line no-continue
    const storedCrossPageEvent = JSON.parse(event);
    localStorage.removeItem(key);

    if (storedCrossPageEvent) dataLayerPromises.push(fireDataLayerEvent(storedCrossPageEvent));
  }

  await Promise.all(dataLayerPromises);
};

// Use this function to queue up a cross page event, as a replacement for dataLayer.push
const fireCrossPageEvent = (key: string, event: Record<string, unknown>): void => {
  if (!localStorage) return;
  localStorage.setItem(`${CROSS_PAGE_EVENTS_NAMESPACE}${key}`, JSON.stringify(event));
};

// Removes an event queued by fireCrossPageEvent before it gets dispatched. Useful when an
// event is queued ahead of a redirect that may not happen (e.g. the action fails instead).
export const removeCrossPageEvent = (key: string): void => {
  if (!localStorage) return;
  localStorage.removeItem(`${CROSS_PAGE_EVENTS_NAMESPACE}${key}`);
};

/**
 * This function uses the "store" verbiage instead of "fire" because it more accurately
 * describes what the fireCrossPageEvent function is actually doing.
 *
 * Events stored under the same key overwrite each other, so pass a `key` when two
 * events with one name can be pending at once.
 */
export const storeCrossPageAnalyticsEvent = (
  analyticsEvent: AnalyticsEvent,
  key: string = analyticsEvent.eventName
): void => {
  const dataLayerEvent = analyticsEventToDataLayerEvent(analyticsEvent);
  fireCrossPageEvent(key, dataLayerEvent);
};

export default fireCrossPageEvent;
